// Companion slice to MCRO001-F0085 (choreo issue #46): the stage information
// surface the owner reported as missing. The rail (DemonstrationTimingRail)
// draws WHERE each saved map sits; this panel states WHAT is mapped and in
// what state, honestly, from the SAME projection the rail already consumes
// (readApprovedMappedDemonstrations / readServedInvalidDemonstrations — passed
// in by RoutineCanvasEditor; this component never refetches).
//
// Degradation vocabulary is F0085's, verbatim: 'media-loading' /
// 'media-unavailable' (a media-reference outcome, never a dropped map) and
// errorState 'invalid-map' (a served record whose timing map failed hard
// validation). Anchor resolution mirrors the rail's own rule (exact complete-
// bar endpoint match against the same authoredBars prop the rail receives), so
// a line the rail draws as anchor-unresolved is described the same way here.
//
// Honesty rules (F0085 patterns):
//   - not settled           → "Loading…", never a fake empty
//   - list fetch failed     → a retryable error, never a fake list
//   - settled and empty     → a truthful "no demonstrations mapped" state
//   - degraded rows         → state WHY, with the saved map still described
//
// Media identity: the demonstrations payload carries no human-readable media
// title (only mediaId + renditionId), so rows show those identifiers rather
// than inventing a name or issuing a second fetch. A choreo-side join of the
// media title into GET …/demonstrations is the recorded follow-up.

import React from 'react';

const DEGRADED_COPY = {
  'media-loading': 'Map saved — video is still loading; counts are shown from the saved map.',
  'media-unavailable': 'Map saved — video is unavailable; counts are shown from the saved map.',
};

// The rail's own anchor rule (DemonstrationTimingRail.js barIndexAtCount):
// exact endpoint matches against this revision's complete 8-count bars.
function completeBarsFor(authoredBars, revisionId) {
  return (authoredBars || []).filter((bar) => bar?.revisionId === revisionId
    && Number.isInteger(bar?.startCount) && Number.isInteger(bar?.endCount)
    && bar.endCount - bar.startCount === 7);
}

function anchorsResolved(timingMap, completeBars) {
  if (!timingMap) return false;
  const start = completeBars.some((bar) => bar.startCount === timingMap.startCount);
  const end = completeBars.some((bar) => bar.endCount === timingMap.endCount);
  return start && end;
}

function barCoverage(timingMap, completeBars) {
  return completeBars.filter((bar) => bar.endCount >= timingMap.startCount
    && bar.startCount <= timingMap.endCount).length;
}

function rowState(candidate, completeBars) {
  if (candidate?.errorState === 'invalid-map') {
    return {
      kind: 'invalid-map',
      text: 'Saved map failed validation — timing cannot be shown on this canvas.',
    };
  }
  const map = candidate?.timingMap;
  if (!map) {
    return { kind: 'unknown', text: 'Saved map details are unavailable for this demonstration.' };
  }
  if (candidate?.degraded && DEGRADED_COPY[candidate.degraded]) {
    return { kind: candidate.degraded, text: DEGRADED_COPY[candidate.degraded] };
  }
  if (!anchorsResolved(map, completeBars)) {
    return {
      kind: 'anchor-unresolved',
      text: `Saved counts ${map.startCount}–${map.endCount} cannot be resolved to complete bars on this revision.`,
    };
  }
  return { kind: 'saved', text: 'Map saved and approved.' };
}

function StageRow({ candidate, completeBars, publishedRevisionId, canEdit }) {
  const map = candidate?.timingMap;
  const state = rowState(candidate, completeBars);
  const counts = map
    ? `Counts ${map.startCount}–${map.endCount} (${map.endCount - map.startCount + 1} counts)`
    : 'Counts unavailable';
  const bars = map ? `${barCoverage(map, completeBars)} complete ${barCoverage(map, completeBars) === 1 ? 'bar' : 'bars'} covered` : '';
  const linkage = candidate?.revisionId && candidate.revisionId === publishedRevisionId
    ? 'Bound to the published revision.'
    : 'Bound to the current draft revision (not the published revision).';
  const editable = canEdit && candidate?.canEditTiming === true;
  return (
    <li
      className={`mc-stage-info-row is-${state.kind}`}
      data-demonstration-id={candidate?.demonstrationId || ''}
      data-state={state.kind}
    >
      <header className="mc-stage-info-row-head">
        <strong>{candidate?.sequenceLabel || 'Demonstration'}</strong>
        <span className="mc-stage-info-pill">{state.kind}</span>
      </header>
      <p className="mc-stage-info-identity">
        Video {candidate?.mediaId || 'unknown'} · rendition {candidate?.rendition?.renditionId || 'unknown'}
        {candidate?.rendition && Number.isInteger(candidate.rendition.version) ? ` (version ${candidate.rendition.version})` : ''}
      </p>
      <p className="mc-stage-info-counts">{counts}{bars ? ` · ${bars}` : ''}</p>
      <p className="mc-stage-info-state" data-state-text={state.kind}>{state.text}</p>
      <p className="mc-stage-info-authorisation">{editable
        ? 'You can edit this timing map.'
        : 'Read-only for you.'}</p>
      <p className="mc-stage-info-linkage">{linkage}</p>
    </li>
  );
}

export default function StageInformationPanel({
  candidates = [],
  errorCandidates = [],
  authoredBars = [],
  revisionId = '',
  publishedRevisionId = '',
  canEdit = false,
  loadState = { settled: true, failed: false },
  onRetry,
}) {
  const completeBars = completeBarsFor(authoredBars, revisionId);
  const retryable = typeof onRetry === 'function';
  return (
    <aside className="mc-stage-info" role="region" aria-label="Stage information">
      <header className="mc-stage-info-head">
        <span className="mc-kicker">Stage information</span>
        <h2>What is mapped</h2>
      </header>
      {loadState?.failed === true ? (
        <div className="mc-alert mc-stage-info-error" role="alert" data-state="list-error">
          <p>The demonstration list could not be loaded, so what is mapped cannot be shown. No demonstration information is available right now.</p>
          {retryable && <button type="button" className="mc-quiet" onClick={onRetry}>Retry</button>}
        </div>
      ) : loadState?.settled === false ? (
        <div className="mc-stage-info-loading" role="status" data-state="list-loading">
          Loading the demonstration map…
        </div>
      ) : (candidates.length === 0 && errorCandidates.length === 0) ? (
        <div className="mc-stage-info-empty" role="status" data-state="empty">
          <strong>No demonstrations are mapped.</strong>
          <span>This revision has no demonstration videos mapped to its counts yet.</span>
        </div>
      ) : (
        <>
          <p className="mc-stage-info-summary" data-state="populated">
            {candidates.length + errorCandidates.length === 1
              ? '1 demonstration is mapped on this canvas.'
              : `${candidates.length + errorCandidates.length} demonstrations are mapped on this canvas.`}
          </p>
          <ul className="mc-stage-info-rows">
            {[...candidates, ...errorCandidates].map((candidate) => (
              <StageRow
                key={candidate.demonstrationId}
                candidate={candidate}
                completeBars={completeBars}
                publishedRevisionId={publishedRevisionId}
                canEdit={canEdit}
              />
            ))}
          </ul>
        </>
      )}
    </aside>
  );
}
